import { memo, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';
import cn from 'classnames';
import { IAdContent, IContentItemClass } from 'src/interface';
import { Logger, UrlUtils } from 'src/utils';
import { adNativeSelector, isNoAdSeclector } from 'src/selectors/ad';
import { Element, Image, Title } from 'src/components';
import Card from 'src/components/card';
import { ContentNormalItem } from 'src/modules/content/components/item/normal';
import { useTracking } from './useTracking';
import { useTrackingActive } from './useTrackingActive';
import ReportButton from './ReportButton';
import { isMiniappSelector, pageSelector } from 'src/selectors/app';
import { PAGE_TYPE } from 'src/constants';

export const AdNativeItem = ({
    zoneId,
    tracking,
    lazyloadImage = true,
    className = '',
    ContentItemClass = {},
}: {
    zoneId: string;
    tracking?: string;
    lazyloadImage?: boolean;
    index?: number;
    className?: string;
    ContentItemClass?: IContentItemClass;
}) => {
    const page = useSelector(pageSelector);
    const adNatives = useSelector(adNativeSelector(zoneId));
    const isIframe = useSelector(isMiniappSelector);
    const isNoAd = useSelector(isNoAdSeclector);
    const [adNative, setAd] = useState<IAdContent>();
    const trackingRef = useTracking(zoneId, tracking, adNative);
    const trackingActiveRef = useTrackingActive(adNative);
    const [fbLandingId, setFbLandingId] = useState<number>(0);
    const isAdInSection = className === 'section-ad-native';

    useEffect(() => {
        const adData = adNatives && adNatives[0];
        if (!adNative && adData) {
            if (adData.title && adData.clickurl) {
                Logger.info('update adNative | ', zoneId, adData.title);
                setAd(adData);
            }
        }
    }, [adNatives]);

    const handleClick = useCallback((fbLandingId: any) => {
        setFbLandingId(fbLandingId);
    }, []);

    if (!adNative && page.type === PAGE_TYPE.HOME) {
        return (
            <Element
                className={cn(
                    'empty mt-[12px] mb-[12px] border-t border-solid border-[#e9ecef]',
                    className,
                )}
            />
        );
    }

    if (isNoAd || !adNative) {
        return null;
    }

    const item = {
        title: adNative.title,
        url: adNative.tracking.click[0],
        redirectUrl: adNative.tracking.click[0],
        thumb: adNative.mediaurls,
        sourceId: adNative.ext?.sourceId || '',
    };

    const onClick = (evt: React.MouseEvent) => {
        evt.preventDefault();
        evt.stopPropagation();
        const redirectUrl = UrlUtils.getParamUrl('redirectUrl', item.url) || '';
        if (redirectUrl.indexOf('form.oa.zalo.me') > -1) {
            (window as any).ZJSBridge.Zalo.openInApp(item.url, (e: any) => {});
        } else {
            if (isIframe) {
                window.parent.postMessage(
                    { type: 'OPEN_LINK_FROM_BAOMOI', url: item.url },
                    'https://h5.zdn.vn',
                );
            } else {
                window.open(item.url, '_blank');
            }
        }
    };

    const renderItem = (image: string, text: string) => {
        return (
            <Card className={'items-center h-[100px]'}>
                <Card.Image className="w-[50px] h-[50px] p-0 bg-none">
                    <Image
                        src={`https://baomoi-static.bmcdn.me/mobile/styles/img/${image}.png`}
                        alt=""
                    />
                </Card.Image>
                <Card.Content>
                    <Title className={'text-[1.7rem] leading-[23px]'}>
                        {text}
                    </Title>
                    <Element
                        className={
                            'text-[1.4rem] leading-[20px] text-[#6c757d] mt-[5px]'
                        }
                    >
                        Chúng tôi sẽ cố gắng hiển thị quảng cáo phù hợp với bạn
                    </Element>
                </Card.Content>
            </Card>
        );
    };

    const renderAdNative = () => {
        switch (fbLandingId) {
            case 2:
                return (
                    <Element
                        className={cn(
                            'mt-[12px] pt-[12px]',
                            'border-t border-solid border-[#e9ecef]',
                            'md:mt-0 md:pt-0 md:border-t-0',
                        )}
                    >
                        {renderItem('adtima-mess-text', 'Đã ẩn quảng cáo')}
                    </Element>
                );
            case 3:
                return (
                    <Element
                        className={cn(
                            'mt-[12px] pt-[12px]',
                            'border-t border-solid border-[#e9ecef]',
                            'md:mt-0 md:pt-0 md:border-t-0',
                        )}
                    >
                        {renderItem(
                            'adtima-report-text',
                            'Cảm ơn bạn đã gửi báo cáo',
                        )}
                    </Element>
                );
            default:
                return (
                    <Element
                        className={twMerge(
                            'mt-[12px] pt-[12px]',
                            'border-t border-solid border-[#e9ecef]',
                            'md:mt-0 md:pt-0 md:border-t-0',
                            cn({
                                ['mb-[12px] pb-[12px] border-b']: isAdInSection,
                            }),
                        )}
                        domRef={trackingActiveRef}
                        data-id={zoneId}
                    >
                        <ContentNormalItem
                            onClick={onClick}
                            domRef={trackingRef}
                            lazyloadImage={lazyloadImage}
                            item={item as any}
                            Footer={Footer}
                            ContentItemClass={Object.assign(
                                {},
                                ContentItemClass,
                                {
                                    CardFooterClass:
                                        'text-[1.3rem] text-[#e8a677]',
                                    CardContentClass: 'flex-grow',
                                },
                            )}
                            newTab
                        />
                    </Element>
                );
        }
    };
    const Footer = (
        <>
            <Element
                className={cn(
                    'inline-block',
                    'max-w-[120px] h-[20px]',
                    'leading-[20px]',
                    'truncate',
                )}
            >
                {adNative.displayUrl}
            </Element>
            <Element>Tài trợ</Element>
            <ReportButton onSubmit={handleClick} item={item} />
            <img
                src={adNative.tracking.impression[0]}
                width="1"
                height="1"
                style={{
                    width: '1px',
                    height: '1px',
                    position: 'absolute',
                    top: '-1px',
                    bottom: '-1px',
                }}
                alt=""
            />
        </>
    );

    return <Element className={className}>{renderAdNative()}</Element>;
};

export default memo(AdNativeItem);
